import {
  recordSoftPromptAccepted,
  recordSoftPromptClosed,
  recordSoftPromptDeclined,
  recordSoftPromptLater,
  recordSoftPromptShown,
  shouldShowSoftPrompt,
  type SoftPromptOptions,
} from "./soft-prompt";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  E-MAIL OPT-IN NUDGE — cadence du 3e toast d'activation (canal e-mail)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Déclencheur : J+1 dans l'espace (jamais au chargement, après interaction —
 *  règle web.dev). La préférence activée se répercute sur la matrice
 *  catégorie × canal de Paramètres → Notifications (canal e-mail).
 *
 *  Refus mémorisé : re-proposition seulement après un changement de relation
 *  (achat d'un pack, téléchargement de CV réussi).
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const EMAIL_NUDGE_ID = "email_optin";

const STORAGE_KEY_FIRST_SEEN = "email_optin_first_seen_v1";

/** Délai avant d'armé l'affichage, après la première opportunité (J+1). */
const FIRST_DELAY_MS = 8_000;
/** Durée d'affichage de la carte avant fermeture automatique. */
export const EMAIL_NUDGE_DURATION_MS = 20_000;

export const EMAIL_NUDGE_OPTIONS: SoftPromptOptions = {
  laterCooldownMs: 48 * 60 * 60 * 1000, // « Plus tard » → +48 h
  closeCooldownMs: 7 * 24 * 60 * 60 * 1000, // X → +7 j
  maxImpressions: 3,
  exhaustedCooldownMs: 14 * 24 * 60 * 60 * 1000, // 3 impressions → pause +14 j
  reactivateOn: ["purchase", "cv_completed"],
};

export { FIRST_DELAY_MS as EMAIL_NUDGE_FIRST_DELAY_MS };

/**
 * J+1 : mémorise la première présence dans l'espace et retourne true si le
 * lendemain est atteint. L'appareil porte le compteur (comme le reste).
 */
export const hasReachedEmailNudgeJ1 = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_FIRST_SEEN);
    const DAY_MS = 24 * 60 * 60 * 1000;

    if (!raw) {
      window.localStorage.setItem(STORAGE_KEY_FIRST_SEEN, String(Date.now()));
      return false;
    }

    const firstSeen = Number(raw);
    if (!Number.isFinite(firstSeen)) {
      window.localStorage.setItem(STORAGE_KEY_FIRST_SEEN, String(Date.now()));
      return false;
    }

    return Date.now() - firstSeen >= DAY_MS;
  } catch {
    // Sans localStorage, on n'affiche pas (comportement prudent).
    return false;
  }
};

export const shouldShowEmailNudge = (): boolean =>
  shouldShowSoftPrompt(EMAIL_NUDGE_ID, EMAIL_NUDGE_OPTIONS);

export const recordEmailNudgeShown = () => recordSoftPromptShown(EMAIL_NUDGE_ID);
export const recordEmailNudgeLater = () =>
  recordSoftPromptLater(EMAIL_NUDGE_ID, EMAIL_NUDGE_OPTIONS);
export const recordEmailNudgeClosed = () =>
  recordSoftPromptClosed(EMAIL_NUDGE_ID, EMAIL_NUDGE_OPTIONS);
export const recordEmailNudgeDeclined = () =>
  recordSoftPromptDeclined(EMAIL_NUDGE_ID, EMAIL_NUDGE_OPTIONS);
export const recordEmailNudgeAccepted = () => recordSoftPromptAccepted(EMAIL_NUDGE_ID);
